import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, IconButton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colorTokens, typographyTokens } from '../../app/designTokens';
import { Button } from '../ui/Button';
import { RegisterSectionCard } from './RegisterSectionCard';

const { semantic } = colorTokens;

/**
 * Step 6 — tenant access. `canonicalReady` distinguishes production public
 * readiness from a local simulated terminal state.
 */
export function TenantAccessCard({
  publicUrl,
  canonicalReady,
  copied,
  onCopy,
  testId = 'register-launch-tenant-access',
}) {
  const { t } = useTranslation(['onboarding']);

  return (
    <RegisterSectionCard
      title={t('onboarding:register.wizard.tenant_access')}
      helper={t('onboarding:register.wizard.access_info')}
      testId={testId}
    >
      {publicUrl ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 1.5 }}
        >
          <Typography
            data-testid="register-launch-public-url"
            sx={{
              flex: 1,
              fontFamily: typographyTokens.fontFamilyMono,
              fontSize: 14,
              color: semantic.text.primary,
              wordBreak: 'break-all',
            }}
          >
            {publicUrl}
          </Typography>
          <IconButton
            aria-label={t('onboarding:register.wizard.copy_url')}
            onClick={onCopy}
            data-testid="register-launch-copy-url"
            size="small"
            sx={{ color: semantic.text.secondary }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          {copied ? (
            <Typography sx={{ fontSize: 12, color: semantic.success }}>
              {t('onboarding:register.wizard.url_copied')}
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t('onboarding:register.wizard.access_pending')}
        </Alert>
      )}

      {!canonicalReady ? (
        <Alert severity="info" sx={{ mb: 1.5 }} data-testid="register-launch-access-local-note">
          {t('onboarding:register.wizard.access_local_note')}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {publicUrl ? (
          <Button
            variant="Primary"
            component="a"
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="register-launch-open-tenant"
          >
            {t('onboarding:register.wizard.open_tenant')}
          </Button>
        ) : null}
        <Button
          variant="Ghost"
          component={RouterLink}
          to="/login"
          data-testid="register-launch-go-login"
        >
          {t('onboarding:register.wizard.go_login')}
        </Button>
      </Stack>
    </RegisterSectionCard>
  );
}

export default TenantAccessCard;
